import '@storelocator/map';
import '@storelocator/card';
import '@storelocator/list';
import '@storelocator/gradient-container';

export class StoreLocator extends HTMLElement {
  constructor() {
    super();
    this.stores = { type: 'FeatureCollection', features: [] };
    this.filteredStores = { type: 'FeatureCollection', features: [] };
    this.searchLocation = null;
    this.mapInstance = null;
    this.settings = {};
    this.companyId = this.getAttribute('storemapper-id');
    this.isMapLoading = true;
  }

  static get observedAttributes() {
    return ['storemapper-id'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'storemapper-id' && oldValue !== newValue) {
      this.companyId = newValue;
      this.loadInitialData();
    }
  }

  connectedCallback() {
    if (this.companyId) {
      this.loadInitialData();
    }
  }

  async loadInitialData() {
    await this.loadSettings();
    await this.loadStores();

    this.setupComponents();
    this.render();
  }

  // Fetches user settings
  async loadSettings() {
    try {
      const response = await fetch(
        `https://storemapper-herokuapp-com.global.ssl.fastly.net//api/users/${this.companyId}.js`,
        {
          method: 'GET',
        }
      );
      const data = await response.json();
      this.settings = data;

      this.settings.map_provider = 'mapbox';
      this.settings.start_zoom = this.settings.start_zoom || 12;
      this.settings.search_zoom = this.settings.search_zoom || 12;
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  }

  // Fetches stores
  async loadStores() {
    try {
      const response = await fetch(
        `https://storemapper-herokuapp-com.global.ssl.fastly.net/api/users/${this.companyId}/stores.js`,
        {
          method: 'GET',
        }
      );
      const data = await response.json();
      this.stores = this.toGeoJSON(data.stores);

      this.filteredStores = this.stores;
    } catch (error) {
      console.error('Error loading stores:', error);
    }
  }

  setupComponents() {
    this.searchForm = this.querySelector('store-locator-autocomplete-input');
    this.map = this.querySelector('store-map');
    this.storeList = this.querySelector('store-list');

    // Set up event listeners
    if (this.searchForm) {
      this.searchForm.addEventListener('search', (e) => this.handleSearch(e.detail));
      this.searchForm.addEventListener('location-selected', (e) => {
        // Handle location selected from autocomplete
        const { coordinates } = e.detail;
        const [lat, lng] = coordinates;
        this.handleSearch({
          query: `${lat},${lng}`,
          location: { lat, lng },
        });
      });
      this.searchForm.addEventListener('current-location', (e) => this.handleSearch(e.detail));
    }

    this.map.addEventListener('card-click', (e) => this.handleMarkerClick(e.detail.storeId));
    this.map.addEventListener('map-loaded', (e) => this.handleMapLoaded(e.detail));
    this.storeList.addEventListener('store-select', (e) => this.handleStoreSelect(e.detail));
  }

  async render() {
    if (!this.map || !this.storeList) {
      console.error('Required components missing:', {
        map: !!this.map,
        storeList: !!this.storeList,
        searchForm: !!this.searchForm,
      });
      return;
    }

    // Show loading state
    this.showMapLoadingState();

    this.map.setAttribute('api-key', this.settings.apikey);
    this.map.setAttribute('zoom', this.settings.start_zoom);
    this.map.setAttribute('style-url', this.settings.mapbox_style_url);

    const storeLocatorSearch = localStorage.getItem('store_locator_search') || null;
    if (storeLocatorSearch) {
      this.searchForm.setValue(storeLocatorSearch);
      this.handleSearch({ query: storeLocatorSearch, location: null });

      localStorage.removeItem('store_locator_search');
    }

    let centerCoords;
    if (navigator.geolocation) {
      try {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 5000,
            maximumAge: 0,
          });
        });

        centerCoords = [position.coords.longitude, position.coords.latitude];
      } catch (error) {
        console.log('Browser geolocation failed, falling back to first store location:', error);
        centerCoords = this.getDefaultCenter();
      }
    } else {
      // Browser doesn't support geolocation, fall back to first store or default
      centerCoords = this.getDefaultCenter();
    }

    // this.map.setAttribute('center', JSON.stringify(centerCoords));
    this.map.setAttribute('stores', JSON.stringify(this.filteredStores));

    // Pass data to list
    this.storeList.setAttribute('stores', JSON.stringify(this.filteredStores));
  }

  getDefaultCenter() {
    // Use first store's location if available
    if (this.stores.features && this.stores.features.length > 0) {
      const [lng, lat] = this.stores.features[0].geometry.coordinates;
      return [lng, lat];
    }

    // Default to New York coordinates if no stores available
    return [-73.918859, 40.6975248];
  }

  async handleSearch({ query, location }) {
    if (location) {
      // search by geolocation
      this.searchLocation = location; // {lat, lng}
      this.filterStoresByDistance();
      const center = [location.lng, location.lat];
      this.map.setAttribute('center', JSON.stringify(center));
      this.map.setAttribute('zoom', this.settings.search_zoom);
    } else if (query) {
      // search by address
      const geocoded = await this.geocode(query);
      if (geocoded) {
        this.searchLocation = geocoded.location;
        this.filterStoresByDistance();
        // Center and zoom the map on the geocoded result
        this.map.setAttribute('center', JSON.stringify(geocoded.center));
        this.map.setAttribute('zoom', this.settings.search_zoom);
      } else {
        // Fallback to text search if geocoding fails
        this.filterStoresByText(query);
      }
    } else {
      // empty query, reset
      this.filteredStores = this.stores;
      this.searchLocation = null;
      // Clear the selected store when search is cleared
      localStorage.removeItem('viewStoreId');
    }
    this.map.setAttribute('stores', JSON.stringify(this.filteredStores));
    this.storeList.setAttribute('stores', JSON.stringify(this.filteredStores));

    if (this.getAttribute('metaobject_stores')) {
      const metaStores = JSON.parse(this.getAttribute('metaobject_stores'));
      this.storeList.setAttribute('metaStores', JSON.stringify(metaStores));
    }

    // Auto-select the first store in search results
    this.autoSelectFirstStore();
  }

  async geocode(address) {
    const key = this.settings.apikey;

    try {
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
        address
      )}.json?access_token=${key}&limit=1`;
      const response = await fetch(url);
      const data = await response.json();
      if (data.features.length > 0) {
        const [lng, lat] = data.features[0].center;
        return { location: { lat, lng }, center: [lng, lat] };
      }
    } catch (error) {
      console.error('Geocoding failed:', error);
    }
    return null;
  }

  filterStoresByText(query) {
    if (!query) {
      this.filteredStores = this.stores;
      return;
    }
    const lowerQuery = query.toLowerCase();
    this.filteredStores = {
      ...this.stores,
      features: this.stores.features.filter((store) => {
        const searchable = `${store.properties.name} ${store.properties.address}`.toLowerCase();
        return searchable.includes(lowerQuery);
      }),
    };
  }

  filterStoresByDistance() {
    if (!this.searchLocation) return;
    const range = this.settings.default_search_radius || 25; // in miles, or get from settings

    const featuresWithDistance = this.stores.features.map((store) => {
      const [lng, lat] = store.geometry.coordinates;
      const distance = this.getDistance(lat, lng, this.searchLocation.lat, this.searchLocation.lng);
      return { ...store, properties: { ...store.properties, distance } };
    });

    const nearby = featuresWithDistance
      .filter((store) => store.properties.distance < range)
      .sort((a, b) => a.properties.distance - b.properties.distance);

    this.filteredStores = { ...this.stores, features: nearby };
  }

  handleMarkerClick(storeId) {
    const store = this.filteredStores.features.find((s) => s.properties.custom_field_1 === storeId);
    if (store) {
      this.reorderStoresWithSelectedFirst(storeId);

      this.storeList.highlightStore(storeId);
      this.dispatchEvent(new CustomEvent('store-selected', { detail: store }));
    }
  }

  reorderStoresWithSelectedFirst(selectedStoreId) {
    if (!this.filteredStores || !this.filteredStores.features) return;

    const selectedStore = this.filteredStores.features.find((s) => s.properties.custom_field_1 === selectedStoreId);
    const otherStores = this.filteredStores.features.filter((s) => s.properties.custom_field_1 !== selectedStoreId);

    if (selectedStore) {
      this.filteredStores = {
        ...this.filteredStores,
        features: [selectedStore, ...otherStores],
      };

      this.storeList.setAttribute('stores', JSON.stringify(this.filteredStores));
    }
  }

  handleStoreSelect({ id, viewOnMap }) {
    const store = this.filteredStores.features.find((s) => s.properties.custom_field_1 === id);
    if (store) {
      this.reorderStoresWithSelectedFirst(id);

      if (viewOnMap) {
        this.map.panToStore(store);
      }
      this.dispatchEvent(new CustomEvent('store-selected', { detail: store }));
    }
  }

  autoSelectFirstStore() {
    if (!this.filteredStores || !this.filteredStores.features || this.filteredStores.features.length === 0) {
      return;
    }

    if (this.filteredStores.features.length === this.stores.features.length) {
      return;
    }

    setTimeout(() => {
      const firstStore = this.filteredStores.features[0];
      if (firstStore) {
        const storeId = firstStore.properties.custom_field_1;

        // Set the selected store in localStorage (this is what the store-list component uses)
        localStorage.setItem('viewStoreId', storeId);
        this.handleStoreSelect({ id: storeId, viewOnMap: true });

        const selectEvent = new CustomEvent('store-select', {
          detail: { storeId: storeId },
          bubbles: true,
        });
        this.storeList.dispatchEvent(selectEvent);

        if (this.storeList && this.storeList.render) {
          this.storeList.render();
        }
      }
    }, 500);
  }

  toGeoJSON(data) {
    return {
      type: 'FeatureCollection',
      features: data.map((store, index) => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [store.longitude, store.latitude],
        },
        properties: { ...store, idx: index },
      })),
    };
  }

  // From storemapper.js
  getDistance(lat1, lon1, lat2, lon2) {
    var R = 6371; // Radius of the earth in km
    var dLat = ((lat2 - lat1) * Math.PI) / 180;
    var dLon = ((lon2 - lon1) * Math.PI) / 180;
    var a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    var d = R * c; // Distance in km
    return this.settings.use_km ? d : d * 0.621371; // convert to miles
  }

  getApiBaseUrl() {
    return 'https://storemapper-herokuapp-com.global.ssl.fastly.net';
  }

  handleMapLoaded() {
    this.isMapLoading = false;
    this.hideMapLoadingState();
  }

  showMapLoadingState() {
    if (!this.map) return;

    // Show the loading overlay that's already in the DOM
    const loadingOverlay = this.map.querySelector('.store-locator__map-loading');
    if (loadingOverlay) {
      loadingOverlay.classList.remove('hidden');
    }
  }

  hideMapLoadingState() {
    if (!this.map) return;

    // Hide the loading overlay
    const loadingOverlay = this.map.querySelector('.store-locator__map-loading');
    if (loadingOverlay) {
      loadingOverlay.classList.add('hidden');
    }
  }

  jsonp(url) {
    return new Promise((resolve, reject) => {
      const callbackName = /callback=([a-zA-Z0-9_]+)/.exec(url)[1];
      window[callbackName] = function (data) {
        // delete window[callbackName];
        // document.body.removeChild(script);
        resolve(data);
      };
      const script = document.createElement('script');
      script.src = url;
      script.onerror = reject;
      document.body.appendChild(script);
    });
  }
}

customElements.define('store-locator', StoreLocator);
